import { useCallback } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLogError } from '@/src/common/hooks/useLogError'
import * as Stores from '../stores'
import * as Form from '../form'
import { z } from 'zod/v4'
import { ResolverError, ResolverOptions, ResolverResult, ResolverSuccess } from 'react-hook-form'
import { Api } from '@/src/store'
import { useEvent } from 'tamagui'

const AddressEntryFormSchema = Form.CardApplicationFormSchema.pick({
  primaryAddress: true,
  mailingAddress: true,
  isMailingAddressDifferent: true,
})

type AddressEntryFormValues = z.infer<typeof AddressEntryFormSchema>

const DefaultErrorResult = {
  values: {},
  errors: {
    primaryAddress: {
      message: `We weren't able to validate your primary address at this time, please try again`,
      type: 'required',
    },
  },
} as const satisfies ResolverResult<AddressEntryFormValues>

export function useAddressValidator() {
  const logError = useLogError()
  const [validateAddress] =
    Stores.CardApplicationApi.endpoints['POST /card/onboarding/validateCustomer'].useMutation()
  const [updateUserAddress] = Api.endpoints['POST /user/address'].useMutation()
  const cardApplication = Stores.useCardApplicationFormStore((s) => s.cardApplication)
  const cardApplicationForm = Stores.useCardApplicationFormStore((s) => s.cardApplicationForm)
  const { firstName, lastName, dob } = cardApplicationForm

  const addressValidator = useCallback(
    async (
      dataToValidate: AddressEntryFormValues,
      context: unknown,
      options: ResolverOptions<AddressEntryFormValues>,
    ): Promise<ResolverResult<AddressEntryFormValues>> => {
      if (!cardApplication) {
        // If there's no card application, how did we even _get_ here?!
        console.warn('No card application found')
        return DefaultErrorResult
      }

      if (!dob) {
        // DOB should never be missing but because we don't have a clean way to
        // represent "unset" dates that aren't `undefined`, the form type allows
        // this to be nullish. In reality, this branch should never be hit.
        return DefaultErrorResult
      }

      if (cardApplication.platform === 'HIGHNOTE') {
        logError('Highnote address attempted to be validated on loanpro stack')
        return DefaultErrorResult
      }

      const baseResolver = zodResolver(AddressEntryFormSchema)
      const baseResult = await baseResolver(dataToValidate, context, options)

      if (Object.keys(baseResult.errors).length > 0) {
        return baseResult
      }

      const { primaryAddress, mailingAddress } = dataToValidate

      // API is validating _customer_, not just addresses.
      const result = await validateAddress({
        body: {
          platform: cardApplication.platform,
          firstName,
          lastName,
          primaryAddress: {
            ...primaryAddress,
            street2: primaryAddress.street2 || undefined, // Coalesce empty string to undefined
          },
          mailingAddress: mailingAddress
            ? {
                ...mailingAddress,
                street2: mailingAddress.street2 || undefined, // Coalesce empty string to undefined
              }
            : undefined,
          dateOfBirth: dob,
        },
      })

      if (!result.data) {
        // If we don't have a response, something went wrong with the API call.
        // Log the error and return a generic message for the user to try again.
        logError(result.error)
        return DefaultErrorResult
      }

      if (
        result.data.primaryAddress.isValid &&
        (!mailingAddress || result.data.mailingAddress.isValid)
      ) {
        return {
          values: {
            primaryAddress: result.data.primaryAddress.corrected ?? primaryAddress,
            ...(mailingAddress && {
              mailingAddress: result.data.mailingAddress.corrected ?? mailingAddress,
            }),
            isMailingAddressDifferent: dataToValidate.isMailingAddressDifferent,
          },
          errors: {},
        } satisfies ResolverSuccess<AddressEntryFormValues>
      }

      return {
        values: {},
        errors: {
          ...(!result.data.primaryAddress.isValid && {
            primaryAddress: {
              message: `We weren't able to validate your primary address at this time, please try again`,
              type: 'required',
            },
          }),
          ...(mailingAddress &&
            !result.data.mailingAddress.isValid && {
              mailingAddress: {
                message: `We weren't able to validate your mailing address at this time, please try again`,
                type: 'required',
              },
            }),
        },
      } satisfies ResolverError<AddressEntryFormValues>
    },
    [validateAddress, logError, cardApplication, firstName, lastName, dob],
  )

  const saveUserAddress = useEvent(async (data: AddressEntryFormValues) => {
    const { street2, ...otherAddressFields } = data.primaryAddress
    const addressPayload = street2 ? { ...otherAddressFields, street2 } : otherAddressFields

    const result = await updateUserAddress({
      body: addressPayload,
    })

    if (result.error) {
      throw new Error(
        "We weren't able to validate your primary address at this time, please try again",
      )
    }

    return result
  })

  return {
    addressValidator,
    saveUserAddress,
  }
}
